/**
 * Cross-tab synchronization utility for logout events
 *
 * Uses BroadcastChannel API to notify all open tabs when a logout event occurs.
 * This ensures that when a user logs out in one tab, all other tabs are
 * immediately notified and can respond appropriately (e.g., redirect to login).
 */

import {logError} from "./logging";

const CHANNEL_NAME = 'rapids-auth-channel';

// Unique per tab/page context. Lets listenForAuthEvents ignore events that
// originated from this same tab, since BroadcastChannel delivers a message
// to every other channel object with the same name -- including ones in the
// same tab -- not just channels in other tabs.
const TAB_ID = typeof crypto !== 'undefined' && crypto.randomUUID
  ? crypto.randomUUID()
  : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export type AuthEventType = 'logout';

export interface AuthEvent {
  type: AuthEventType;
  timestamp: number;
  senderId?: string;
}

export type AuthEventListener = (event: AuthEvent) => void;

/**
 * Broadcast an authentication event to all tabs
 *
 * @param eventType - Type of authentication event to broadcast
 * @returns true if broadcast succeeded, false otherwise
 */
export function broadcastAuthEvent(eventType: AuthEventType): boolean {
  try {
    if (typeof BroadcastChannel === 'undefined') {
      return false;
    }

    const event: AuthEvent = {
      type: eventType,
      timestamp: Date.now(),
      senderId: TAB_ID,
    };

    const channel = new BroadcastChannel(CHANNEL_NAME);
    channel.postMessage(event);
    channel.close();
    return true;
  } catch (error) {
    logError(error, {
      message: "Failed to broadcast auth event"
    });
    return false;
  }
}

/**
 * Listen for authentication events from other tabs
 *
 * @param listener - Callback function to handle auth events
 * @returns Cleanup function to remove the listener
 */
export function listenForAuthEvents(listener: AuthEventListener): () => void {
  if (typeof BroadcastChannel === 'undefined') {
    return () => {};
  }

  try {
    const channel = new BroadcastChannel(CHANNEL_NAME);

    const handleMessage = (event: MessageEvent<AuthEvent>) => {
      // Ignore events broadcast by this same tab -- BroadcastChannel delivers
      // messages to every other channel object with this name, including
      // ones opened in the same tab. This listener is only meant to react to
      // OTHER tabs; this tab already handles its own sign-out flow directly.
      if (event.data && event.data.type && event.data.senderId !== TAB_ID) {
        listener(event.data);
      }
    };

    channel.addEventListener('message', handleMessage);

    return () => {
      try {
        channel.removeEventListener('message', handleMessage);
        channel.close();
      } catch (error) {
        logError(error, {
          message: "Error during auth event listener cleanup"
        });
      }
    };
  } catch (error) {
    logError(error, {
      message: "Failed to set up auth event listener"
    });
    return () => {};
  }
}
